import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  Diamond,
  Download,
  GraduationCap,
  HardHat,
  Pencil,
  Plus,
  Search,
  Trash2,
  UserRound,
  Users,
} from "lucide-react";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import BadgeBoxCst from "@components/BadgeBoxCst";
import InputCst from "@components/InputCst/InputCst";
import SelectCst from "@components/InputCst/SelectCst";
import TabsCst from "@components/TabsCst";
import ListItemCardCst from "@components/ListItemCardCst";
import PaginatorCst from "@components/PaginatorCst";
import ConfirmDialogCst from "@components/ConfirmDialogCst";
import { openToast as showToast } from "@utils/toastStore";
import UploadTrainingModalV2Cst from "@components/UploadTrainingModalV2Cst";
import { usePaginatedList } from "@hooks/usePaginatedList";
import { useFetchDetail, useFetchDataDetail } from "@hooks/useFetchDetail";
import { ServicesURL, getQueryString, downloadBlobFile } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { CUSTOM_FIELD_KEYS } from "../Wizard/types";
import AddToStaticGroupsModal from "../components/AddToStaticGroupsModal";
import {
  DetailAccordion,
  DownloadCard,
  downloadGridSx,
  FieldGroup,
  InfoItem,
  SectionHeader,
  searchBoxSx,
  selectBoxSx,
} from "../components/detailLayout";

const PAGE_SIZE = 10;

interface GroupCardData {
  id: string | number;
  name: string;
  type: string;
  createdAt?: any;
  initiatives?: number;
  enrolledUsers?: number;
  learningObjects?: number;
}

const GroupCard: React.FC<{
  group: GroupCardData;
  onDetail: () => void;
}> = ({ group, onDetail }) => {
  const theme = useTheme();
  const { t } = useTranslation("user-detail-v2");

  const metrics = [
    {
      icon: <Diamond size={16} />,
      value: group.initiatives ?? 0,
      label: t("groups.initiatives"),
    },
    {
      icon: <Users size={16} />,
      value: group.enrolledUsers ?? 0,
      label: t("groups.enrolledUsers"),
    },
    {
      icon: <GraduationCap size={16} />,
      value: group.learningObjects ?? 0,
      label: t("groups.learningObjects"),
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        backgroundColor: theme.palette.action.hover,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "10px",
        p: "20px 24px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>
            {group.name}
          </Typography>
          <Typography
            sx={{ fontSize: "13px", color: theme.palette.text.tertiary }}
          >
            ID: {group.id}
          </Typography>
        </Box>
        {group.createdAt && (
          <Box
            sx={{
              backgroundColor: theme.palette.text.primary,
              color: theme.palette.background.default,
              borderRadius: "4px",
              px: "8px",
              py: "2px",
              fontSize: "13px",
              fontWeight: 700,
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {t("groups.createdOn")} {formatDate(group.createdAt)}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 3,
            flexWrap: "wrap",
            color: theme.palette.text.primary,
          }}
        >
          {metrics.map((m, i) => (
            <Box
              key={i}
              sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
            >
              {m.icon}
              <Typography sx={{ fontSize: "14px" }}>
                {m.value} {m.label}
              </Typography>
            </Box>
          ))}
        </Box>
        <Button
          variant="outlined"
          onClick={onDetail}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            fontWeight: 600,
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {t("groups.details")}
        </Button>
      </Box>
    </Box>
  );
};

const formatDate = (value: any): string => {
  if (value === null || value === undefined || value === "") return "";
  const date = new Date(typeof value === "number" ? value : String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("it-IT");
};

const LO_TYPE_BY_TAB: Record<string, string> = {
  activities: "ACTIVITY",
  courses: "COURSE",
  paths: "PATH",
};

const UserDetail: React.FC = () => {
  const { t } = useTranslation("user-detail-v2");
  const { t: tw } = useTranslation("user-wizard-v2");
  const theme = useTheme();
  const navigate = useNavigate();
  const { id: corporateId = "", userId = "" } = useParams();

  const { dataDetail: user, isLoading } = useFetchDetail(
    ServicesURL.customer,
    `${userId}?corporateId=${corporateId}`,
    PagesID["corporate.customers"],
  );

  const { dataDetail: customFieldsData } = useFetchDataDetail(
    `${ServicesURL.getCorporateCustomFields}?corporate_id=${corporateId}`,
    PagesID["corporate.customFields"],
  );

  const fullName = [user?.name, user?.surname].filter(Boolean).join(" ").trim();
  const isActive = String(user?.status || "").toLowerCase() !== "suspended";

  const yes = tw("common.yes");

  const roles: string[] = useMemo(() => {
    const raw = user?.roles;
    if (!Array.isArray(raw)) return [];
    return raw
      .map((r: any) =>
        typeof r === "string" ? r : r?.label || r?.value || r?.name,
      )
      .filter(Boolean);
  }, [user]);

  const badges: {
    id: string | number;
    name: string;
    certificateId?: string | number;
    objectId?: string | number;
  }[] = [];
  const initiatives: { id: string | number; name: string; status?: string }[] =
    [];
  const { dataDetail: userGroupsData, mutate: mutateUserGroups } =
    useFetchDataDetail(
      corporateId && userId
        ? `${ServicesURL.corporate}/${corporateId}/users/${userId}/groups?type=STATIC`
        : null,
    );

  const groups: GroupCardData[] = useMemo(() => {
    const list = Array.isArray(userGroupsData)
      ? userGroupsData
      : userGroupsData?.groups_list || [];
    return list.map((g: any) => ({
      id: g.id,
      name: g.name || "-",
      type: "static",
      createdAt: g.creation_date || g.created_date || g.createdAt,
    }));
  }, [userGroupsData]);

  const [addToGroupsOpen, setAddToGroupsOpen] = useState(false);

  const handleDownloadGroupsCsv = () => {
    const escape = (value: any) =>
      `"${String(value ?? "").replace(/"/g, '""')}"`;
    const header = [
      t("groups.csv.id"),
      t("groups.csv.name"),
      t("groups.csv.type"),
      t("groups.csv.createdAt"),
    ];
    const rows = groups.map((g) =>
      [g.id, g.name, t("groups.csv.type-static"), formatDate(g.createdAt)]
        .map(escape)
        .join(","),
    );
    const bom = "\uFEFF";
    const csvContent = `${bom}${header.map(escape).join(",")}\n${rows.join(
      "\n",
    )}\n`;
    const blob = new Blob([csvContent], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `user-${userId}-groups.csv`;
    link.click();
  };

  const goToEdit = () =>
    navigate(`/v2/corporate/${corporateId}/detail/users-groups/${userId}/edit`);

  const goToGroupDetail = (g: GroupCardData) => {
    const segment = g.type === "dynamic" ? "dynamic-groups" : "groups";
    navigate(
      `/v2/corporate/${corporateId}/detail/users-groups/${segment}/${g.id}`,
    );
  };

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      await fetcher(
        getQueryString(`${ServicesURL.deleteCustomer}`, {
          users_id: String(userId),
          corporate_id: corporateId,
        }),
        MethodType.DELETE,
      );
      showToast({
        severity: "success",
        message: String(t("confirm-dialog.success", { ns: "users-groups-v2" })),
      });
      navigate(`/v2/corporate/${corporateId}/detail/users-groups`);
    } catch (error) {
      console.error(error);
      showToast({
        severity: "error",
        message: String(t("confirm-dialog.error", { ns: "users-groups-v2" })),
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const [downloadingCertId, setDownloadingCertId] = useState<
    string | number | null
  >(null);
  const [downloadingAll, setDownloadingAll] = useState(false);

  const downloadCertificate = async (cert: (typeof badges)[number]) => {
    if (!userId || cert.certificateId == null) return;
    setDownloadingCertId(cert.id);
    try {
      const url = `${ServicesURL.getIssuedCertificate.replace(
        ":certificateId",
        String(cert.certificateId),
      )}?objectId=${cert.objectId ?? ""}&learnerId=${userId}`;
      await downloadBlobFile(
        url,
        null,
        `issued_certificate_lo_${cert.objectId ?? cert.id}_learner_${userId}.pdf`,
        MethodType.GET,
      );
    } catch (error) {
      console.error(error);
    } finally {
      setDownloadingCertId(null);
    }
  };

  const downloadAllCertificates = async () => {
    if (!userId) return;
    const issuedCertificates = badges
      .filter((b) => b.certificateId != null)
      .map((b) => ({
        learnerId: userId,
        objectId: b.objectId ?? b.id,
        issuedCertificateId: b.certificateId,
      }));
    if (issuedCertificates.length === 0) return;
    setDownloadingAll(true);
    try {
      await downloadBlobFile(
        `${ServicesURL.getIssuedCertificatesZip}`,
        { issuedCertificates },
        `issued_certificates_learner_${userId}.zip`,
      );
    } catch (error) {
      console.error(error);
    } finally {
      setDownloadingAll(false);
    }
  };

  const registryItems: InfoItem[] = [
    { label: tw("fields.phone"), value: user?.phone_number },
    { label: tw("fields.address"), value: user?.address },
    { label: tw("fields.gender"), value: user?.gender },
    { label: tw("fields.fiscalCode"), value: user?.fiscal_code },
    { label: tw("fields.birthPlace"), value: user?.birth_country },
    { label: tw("fields.birthProvince"), value: user?.birth_province },
    { label: tw("fields.birthDate"), value: formatDate(user?.birthday) },
  ];

  const companyItems: InfoItem[] = [
    {
      label: tw("questions.newHireLabel"),
      value: user?.new_hire_flag ? yes : "",
    },
    {
      label: tw("questions.smartWorkingLabel"),
      value: user?.smart_working_flag ? yes : "",
    },
    { label: tw("fields.branch"), value: user?.branch },
    { label: tw("fields.language"), value: user?.language },
    { label: tw("fields.atecoCode"), value: user?.ateco_code },
    { label: tw("fields.countryCode"), value: user?.country_code },
    {
      label: tw("fields.countryDescription"),
      value: user?.country_description,
    },
    { label: tw("fields.supervisor"), value: user?.supervisor },
    {
      label: tw("fields.frameworkCode"),
      value: user?.contractual_framework_code,
    },
    {
      label: tw("fields.frameworkType"),
      value: user?.contractual_framework_type,
    },
    { label: tw("fields.contractType"), value: user?.contract_type },
    { label: tw("fields.ccnl"), value: user?.ccnl },
    { label: tw("fields.hireDate"), value: formatDate(user?.hire_date) },
    {
      label: tw("fields.terminationDate"),
      value: formatDate(user?.termination_date),
    },
    { label: tw("fields.scheduleType"), value: user?.schedule_type },
    {
      label: tw("fields.smartLearningHours"),
      value:
        user?.smart_learning_hours != null
          ? String(user.smart_learning_hours)
          : "",
    },
    {
      label: tw("fields.persona"),
      value: Array.isArray(user?.persona) ? user.persona.join(", ") : "",
    },
  ];

  const customCatalog: Record<string, string> = customFieldsData?.catalog || {};
  const customItems: InfoItem[] = CUSTOM_FIELD_KEYS.filter(
    (k) => customCatalog[k],
  ).map((k) => ({ label: customCatalog[k], value: user?.[k] }));

  const uogItems: InfoItem[] = Array.isArray(user?.uog)
    ? user.uog.map((u: any, i: number) => ({
        label: `${t("sections.uog")} ${i + 1}`,
        value: u?.uog_description || u?.uog,
      }))
    : [];

  const [loTab, setLoTab] = useState("all");
  const [loPage, setLoPage] = useState(1);
  const [loSearch, setLoSearch] = useState("");
  const loUrl = userId
    ? ServicesURL.getLOBasedOnStudentId
        .replace(":idCorporate", corporateId || "1")
        .replace(":studentId", userId)
    : null;
  const {
    items: los,
    totalItems: loTotal,
    totalPages: loPages,
    isLoading: loadingLo,
  } = usePaginatedList(loUrl, {
    page: loPage,
    pageSize: PAGE_SIZE,
    search: loSearch || undefined,
    fieldFilters:
      loTab !== "all" && LO_TYPE_BY_TAB[loTab]
        ? { type: LO_TYPE_BY_TAB[loTab] }
        : undefined,
  });

  const loTabs = [
    { key: "all", label: t("lo.tabs.all") },
    { key: "activities", label: t("lo.tabs.activities") },
    { key: "courses", label: t("lo.tabs.courses") },
    { key: "paths", label: t("lo.tabs.paths") },
  ];

  const [cvSearch, setCvSearch] = useState("");
  const [cvStatus, setCvStatus] = useState("");
  const [cvType, setCvType] = useState("");
  const [cvPage, setCvPage] = useState(1);

  const cvUrl = userId
    ? `${ServicesURL.getSkillBook}/detail?corporateId=${
        corporateId || "1"
      }&learnerId=${userId}${cvStatus ? `&status=${cvStatus}` : ""}`
    : null;
  const {
    items: cvItems,
    totalItems: cvTotal,
    totalPages: cvPages,
    isLoading: loadingCv,
  } = usePaginatedList(
    cvUrl,
    { page: cvPage, pageSize: PAGE_SIZE, search: cvSearch || undefined },
    { responseKey: "content" },
  );

  const { dataDetail: courseTypeData } = useFetchDataDetail(
    ServicesURL.getCourseType,
    PagesID["corporate.customers"],
  );

  const cvTypeOptions = useMemo(() => {
    const raw = Array.isArray(courseTypeData)
      ? courseTypeData
      : courseTypeData?.output || courseTypeData?.content || [];
    return [
      { value: "", label: t("cv.typeAll") },
      ...raw.map((o: any) => ({
        value: String(o.value ?? o.id ?? o.code ?? o.name ?? ""),
        label: String(o.label ?? o.name ?? o.description ?? o.value ?? ""),
      })),
    ];
  }, [courseTypeData, t]);

  const cvStatusOptions = [
    { value: "", label: t("cv.statusAll") },
    { value: "pending", label: t("cv.statusPending") },
    { value: "approved", label: t("cv.statusApproved") },
    { value: "rejected", label: t("cv.statusRejected") },
  ];

  const [uploadOpen, setUploadOpen] = useState(false);

  const filteredCv = useMemo(() => {
    if (!cvType) return cvItems;
    return cvItems.filter(
      (r: any) =>
        String(r.courseType ?? r.trainingType ?? r.type ?? "") === cvType,
    );
  }, [cvItems, cvType]);

  return (
    <AnimationFadeIn>
      <Box
        sx={{
          py: 3,
          pr: 3,
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                "& > .MuiBox-root": { position: "static", p: "20px 0" },
              }}
            >
              <Breadcrumb crumbs={[{ to: "", text: t("breadcrumb-detail") }]} />
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
              <Tooltip title={t("actions.edit")}>
                <IconButton
                  onClick={goToEdit}
                  aria-label={t("actions.edit")}
                  sx={{ color: theme.palette.primary.main }}
                >
                  <Pencil size={20} />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("actions.delete")}>
                <IconButton
                  onClick={() => setDeleteOpen(true)}
                  aria-label={t("actions.delete")}
                  sx={{ color: theme.palette.primary.main }}
                >
                  <Trash2 size={20} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
              mt: 1,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <BadgeBoxCst variant={isActive ? "success" : "error"}>
                {isActive ? t("status.active") : t("status.suspended")}
              </BadgeBoxCst>
              <Typography
                sx={{
                  fontSize: "13px",
                  color: theme.palette.text.tertiary,
                  mt: 1,
                }}
              >
                ID: {userId}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  mt: 2,
                }}
              >
                <Typography sx={{ fontSize: "28px", fontWeight: 700 }}>
                  {isLoading ? "…" : fullName || "-"}
                </Typography>
                <Typography
                  component={user?.email ? "a" : "span"}
                  href={user?.email ? `mailto:${user.email}` : undefined}
                  sx={{
                    fontSize: "14px",
                    color: theme.palette.text.tertiary,
                    textDecoration: user?.email ? "underline" : "none",
                    cursor: user?.email ? "pointer" : "default",
                    width: "fit-content",
                    "&:hover": user?.email
                      ? { color: theme.palette.text.primary }
                      : undefined,
                  }}
                >
                  {user?.email || ""}
                </Typography>
                {roles.length > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <HardHat size={18} color={theme.palette.text.tertiary} />
                    <Typography sx={{ fontSize: "15px" }}>
                      {roles.join(", ")}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
            <Box
              sx={{
                width: 160,
                height: 160,
                borderRadius: "12px",
                border: `1px solid ${theme.palette.divider}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: theme.palette.text.tertiary,
                backgroundColor: theme.palette.action.hover,
                overflow: "hidden",
              }}
            >
              {user?.teacher_picture ? (
                <img
                  src={user.teacher_picture}
                  alt={fullName}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <UserRound size={48} />
              )}
            </Box>
          </Box>
        </Box>

        <DetailAccordion title={t("additionalInfo")}>
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <FieldGroup
                title={t("sections.registry")}
                items={registryItems}
              />
              <FieldGroup
                title={tw("sections.companyInfo")}
                items={companyItems}
              />
              <FieldGroup
                title={tw("sections.otherInfo")}
                items={customItems}
              />
              <FieldGroup title={t("sections.uogTitle")} items={uogItems} />
            </Box>
          )}
        </DetailAccordion>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <SectionHeader
            title={t("badges.title")}
            action={
              <Button
                variant="outlined"
                startIcon={
                  downloadingAll ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <Download size={16} />
                  )
                }
                onClick={downloadAllCertificates}
                disabled={badges.length === 0 || downloadingAll}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  fontWeight: 600,
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                {t("badges.downloadAll")}
              </Button>
            }
          />
          {badges.length > 0 ? (
            <Box sx={downloadGridSx}>
              {badges.map((b) => (
                <DownloadCard
                  key={b.id}
                  title={b.name}
                  id={b.id}
                  loading={downloadingCertId === b.id}
                  onDownload={() => downloadCertificate(b)}
                />
              ))}
            </Box>
          ) : (
            <Typography sx={{ color: theme.palette.text.tertiary }}>
              {t("badges.empty")}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <SectionHeader title={t("initiatives.title")} />
          {initiatives.length > 0 ? (
            initiatives.map((i) => (
              <ListItemCardCst
                key={i.id}
                title={i.name}
                subtitle={{ id: i.id, activityType: i.status }}
                badges={[]}
                actions={[]}
              />
            ))
          ) : (
            <Typography sx={{ color: theme.palette.text.tertiary }}>
              {t("initiatives.empty")}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <SectionHeader
            title={t("groups.title")}
            action={
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexDirection: { xs: "column", sm: "row" },
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<Plus size={16} />}
                  onClick={() => setAddToGroupsOpen(true)}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    fontWeight: 700,
                  }}
                >
                  {t("groups.addToStatic")}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Download size={16} />}
                  onClick={handleDownloadGroupsCsv}
                  disabled={groups.length === 0}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    fontWeight: 600,
                  }}
                >
                  {t("downloadCsv")}
                </Button>
              </Box>
            }
          />
          {groups.length > 0 ? (
            groups.map((g) => (
              <GroupCard
                key={g.id}
                group={g}
                onDetail={() => goToGroupDetail(g)}
              />
            ))
          ) : (
            <Typography sx={{ color: theme.palette.text.tertiary }}>
              {t("groups.empty")}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <SectionHeader title={t("lo.title")} subtitle={t("lo.subtitle")} />

          <TabsCst
            tabs={loTabs}
            activeKey={loTab}
            onChange={(k) => {
              setLoTab(k);
              setLoPage(1);
            }}
          />

          <Box sx={searchBoxSx}>
            <InputCst
              id="user-detail-lo-search"
              label={t("search")}
              value={loSearch}
              onChange={(e: any) => {
                setLoSearch(e.target.value);
                setLoPage(1);
              }}
              endAdornment={
                <Search size={16} color={theme.palette.text.tertiary} />
              }
            />
          </Box>

          {loadingLo ? (
            <CircularProgress />
          ) : los.length === 0 ? (
            <Typography sx={{ color: theme.palette.text.tertiary }}>
              {t("lo.empty")}
            </Typography>
          ) : (
            los.map((raw: any, index: number) => {
              const loId = raw.id ?? raw.activity_id ?? index;
              return (
                <ListItemCardCst
                  key={loId}
                  title={raw.name || raw.title || raw.activity_name || "-"}
                  subtitle={{
                    id: loId,
                    activityType: raw.category || raw.type,
                  }}
                  badges={[]}
                  actions={[]}
                />
              );
            })
          )}

          {loPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <PaginatorCst
                page={loPage}
                pageSize={PAGE_SIZE}
                totalItems={loTotal}
                onPageChange={setLoPage}
              />
            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <SectionHeader
            title={t("cv.title")}
            subtitle={t("cv.subtitle")}
            action={
              <Button
                variant="contained"
                startIcon={<Plus size={16} />}
                onClick={() => setUploadOpen(true)}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  fontWeight: 700,
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                {t("cv.uploadExternal")}
              </Button>
            }
          />
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Box sx={searchBoxSx}>
              <InputCst
                id="user-detail-cv-search"
                label={t("search")}
                value={cvSearch}
                onChange={(e: any) => {
                  setCvSearch(e.target.value);
                  setCvPage(1);
                }}
                endAdornment={
                  <Search size={16} color={theme.palette.text.tertiary} />
                }
              />
            </Box>
            <Box sx={selectBoxSx}>
              <SelectCst
                id="user-detail-cv-status"
                label={t("cv.status")}
                value={cvStatus}
                options={cvStatusOptions}
                onChange={(value: any) => {
                  setCvStatus(value);
                  setCvPage(1);
                }}
              />
            </Box>
            <Box sx={selectBoxSx}>
              <SelectCst
                id="user-detail-cv-type"
                label={t("cv.type")}
                value={cvType}
                options={cvTypeOptions}
                onChange={(value: any) => setCvType(value)}
              />
            </Box>
          </Box>

          {loadingCv ? (
            <CircularProgress />
          ) : filteredCv.length === 0 ? (
            <Typography sx={{ color: theme.palette.text.tertiary }}>
              {t("cv.empty")}
            </Typography>
          ) : (
            filteredCv.map((raw: any, index: number) => {
              const cvId = raw.id ?? raw.requestId ?? index;
              const subParts = [
                raw.courseType || raw.trainingType || raw.type,
                raw.status,
                raw.year,
              ]
                .filter(Boolean)
                .join(" | ");
              return (
                <ListItemCardCst
                  key={cvId}
                  title={
                    raw.courseName ||
                    raw.name ||
                    raw.title ||
                    raw.trainingName ||
                    "-"
                  }
                  subtitle={{ id: cvId, activityType: subParts }}
                  badges={[]}
                  actions={[]}
                />
              );
            })
          )}

          {cvPages > 1 && !cvType && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <PaginatorCst
                page={cvPage}
                pageSize={PAGE_SIZE}
                totalItems={cvTotal}
                onPageChange={setCvPage}
              />
            </Box>
          )}
        </Box>
      </Box>

      <ConfirmDialogCst
        open={deleteOpen}
        title={String(t("delete-item", { ns: "modal" }))}
        description={t("confirm-delete", { ns: "modal" })}
        confirmLabel={String(
          t("confirm-dialog.confirm", { ns: "users-groups-v2" }),
        )}
        cancelLabel={String(
          t("confirm-dialog.cancel", { ns: "users-groups-v2" }),
        )}
        confirmVariant="danger"
        isSubmitting={isDeleting}
        onClose={() => setDeleteOpen(false)}
        onConfirm={confirmDelete}
      />

      <UploadTrainingModalV2Cst
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
      />

      <AddToStaticGroupsModal
        open={addToGroupsOpen}
        onClose={() => setAddToGroupsOpen(false)}
        corporateId={corporateId}
        userId={userId}
        userName={fullName || user?.email || "-"}
        onSuccess={() => mutateUserGroups && mutateUserGroups()}
      />
    </AnimationFadeIn>
  );
};

export default UserDetail;
